import { useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'

gsap.registerPlugin(ScrollTrigger)

/**
 * ScrollReveals - ScrollTrigger entrances for four homepage sections.
 *
 *   #trust     client logos fade in + slide up, 0.1s stagger   (start top 80%)
 *   #reviews   4 .tq-card quotes fade in from the left, 0.15s  (start top 80%)
 *   #services  service cards fade up, 0.1s stagger             (start top 85%)
 *   #why-us    4 stat cards fade up + the numbers count from 0 (start top 85%)
 *
 * ── These sections were ALREADY animated; this replaces, not stacks ────────
 * Both carry [data-reveal], and RevealFixer.jsx runs a per-item cascade on
 * those sections (44px / 780ms, Web Animations API) - for #reviews it already
 * picked up the four .tq-card elements, because .tq-grid is a grid with >=2
 * card-like children, which is exactly what its collectItems() walk looks for.
 * Layering GSAP on top would be a third system animating the same properties,
 * and WAAPI composites ABOVE inline styles, so the two would visibly fight:
 * WAAPI wins for its 780ms, releases, then GSAP's inline transform snaps in.
 *
 * So each section is opted OUT of the old behaviour before GSAP touches it:
 *   - dataset.rvPlayed = '1'  -> RevealFixer.playStagger() bails on that flag
 *   - classList.add('in')     -> satisfies the [data-reveal] CSS (opacity 0 +
 *                                translateY(40px) until .in), so the section
 *                                itself is visible and only the CARDS animate
 * The heading blocks keep their own [data-rv] variants - those target
 * different elements and are not in conflict.
 *
 * Audited per section rather than assumed (measured in-browser):
 *   #trust     [data-reveal] only - cascade did not reach the marquee
 *   #reviews   [data-reveal] + RevealFixer cascade ON the 4 .tq-card
 *   #services  [data-reveal] only (rvPlayed unset, no data-reveal-staggered)
 *   #why-us    [data-reveal] + RevealFixer cascade + runCounters() count-up
 *
 * ── #why-us already had a count-up ───────────────────────────────────────
 * runCounters() in Pentabay.html auto-wires every .stat-num: it parses the
 * displayed value into prefix / number / suffix, then counts up over 1800ms on
 * an IntersectionObserver with its own rAF loop. It had already run - all four
 * stats came back with countInit=1, countObserved=1 and parsed targets
 * 100 / 6 / 5.0 / 85. Adding a GSAP count-up without disabling it would have
 * two loops writing textContent on the same node, which is not a subtle bug: it
 * flickers between two different numbers.
 *
 * It is suppressed by setting BOTH of its guard flags before it runs
 * (see takeOverCounters). The value is re-parsed here with the same regex, so
 * the numbers, decimals and the "+"/"%" suffixes are preserved exactly.
 *
 * ── Timing: why a MutationObserver ────────────────────────────────────────
 * useGSAP runs in a layout effect; HtmlPage injects the homepage in a regular
 * effect, which is later. Observer callbacks are microtasks delivered before
 * paint, so the opt-out lands before RevealFixer's IntersectionObserver (which
 * fires on a subsequent frame) can start the old cascade.
 */

const SECTIONS = [
  {
    id: 'trust',
    // The row, not the whole section - "as the row scrolls into view".
    triggerSel: '.client-carousel',
    itemSel: '.cc-logo',
    from: { opacity: 0, y: 56 },
    stagger: 0.12,
  },
  {
    id: 'reviews',
    triggerSel: '.tq-grid',
    itemSel: '.tq-card',
    // "from left to right" read both ways: each card slides in FROM the left,
    // and they sequence left-to-right (stagger default is DOM order, which
    // matches visual order in this grid).
    from: { opacity: 0, x: -110 },
    stagger: 0.15,
  },
  {
    id: 'services',
    // Same shape as #trust: ServicesCarousel.jsx turns this into a cloned
    // marquee (8 .service-card in source, 16 at runtime), so this MUST go
    // through the pre-hide + live-target path, not gsap.from().
    triggerSel: '.svc-marquee',
    itemSel: '.service-card',
    from: { opacity: 0, y: 68 },
    stagger: 0.12,
    start: 'top 85%',
  },
  {
    id: 'industries-serve',
    // 8 real cards, no clones, not inside a marquee (this WAS a .cc-track
    // marquee once - the markup comment says so - but it is a plain grid now).
    triggerSel: '[data-ind-grid]',
    itemSel: '.ind-card',
    from: { opacity: 0, scale: 0.82 },
    stagger: 0.12,
    start: 'top 85%',
    // initIndustryReveal() in Pentabay.html owns this grid otherwise; see
    // takeOverIndustryGrid.
    legacy: 'industryGrid',
  },
  {
    id: 'why-us',
    // That grid carries only Tailwind utility classes, no hook. Resolved from
    // the stats themselves rather than adding a class to the HTML layer.
    triggerSel: (section) => {
      const n = section.querySelector('.stat-num')
      return n ? n.closest('.grid') : null
    },
    itemSel: '.card',
    from: { opacity: 0, y: 68 },
    stagger: 0.14,
    start: 'top 85%',
    // Count the .stat-num inside each card up from 0, started in step with
    // that card's own fade - "synced with the cards fading in".
    countSel: '.stat-num',
    countDuration: 1.5,
    countEase: 'power1.out',
  },
]

/* 2026-08 intensity pass. Offsets roughly tripled per section (see SECTIONS),
   0.7s -> 0.5s, and back.out so each item passes its resting position and
   settles. These items are all discrete cards, logos and tiles, which is what an
   overshoot suits; the two-attribute gate above (PENDING + ANIM) zeroes their
   hover transitions for the whole tween, so the bigger travel cannot reintroduce
   the dip-and-return that gate exists to prevent.
   cfg.ease lets one section opt out of the shared curve without forking the
   tween; nothing needs it today. */
const DURATION = 0.5
const EASE = 'back.out(1.5)'
const DEFAULT_START = 'top 80%'

/* The end state is derived from whatever `from` declares, rather than writing
   opacity/x/y/scale on every tween. Keeps each tween to the properties it
   actually animates, so clearProps has less to undo and no section picks up a
   stray inline transform it never asked for. */
function resetFor(from) {
  const to = { opacity: 1 }
  if ('x' in from) to.x = 0
  if ('y' in from) to.y = 0
  if ('scale' in from) to.scale = 1
  return to
}

/* Pre-hide via CSS, not via gsap.from()'s immediateRender.
 *
 * This exists because of ClientCarousel. It clones the logo strip with
 * cloneNode to fill the marquee loop, and cloneNode COPIES INLINE STYLES. The
 * first version of this file used gsap.from(items), which writes opacity:0
 * inline on the 11 originals at build time; ClientCarousel then cloned them
 * and produced 22 clones born at opacity:0 that were never tween targets, so
 * they stayed invisible forever. Measured: 33 logos, min opacity 0, 22 with
 * stranded inline styles. (It self-healed on the next resize, which re-clones
 * from the by-then-cleared originals - which is exactly the kind of bug that
 * looks fine locally and ships broken.)
 *
 * A CSS rule keyed on an attribute has neither problem: it covers clones made
 * at ANY time without touching inline styles, and it costs nothing to undo.
 * The tween then resolves its targets live at trigger time, by which point the
 * clones exist and are included. */
const PENDING = 'data-pb-scroll-anim'      // pre-hide, before the trigger fires
const ANIM = 'data-pb-scroll-anim-run'     // tween in flight

/* Built from SECTIONS so the selector list cannot drift from the configs.
 *
 * TWO attributes, and the second one is not decoration. Several of these items
 * carry their own CSS `transition` on transform (the hover lift: .ind-card is
 * `transition: transform .3s ease`, ungated). GSAP writes transform inline,
 * which beats the class rule - but the transition still applies TO those inline
 * writes, so the browser eases toward each frame GSAP sets and the result is a
 * value that lags and overshoots. Measured on .ind-card before this fix: scale
 * read 1.000 -> 0.957 -> 1.000, a dip-and-return instead of the 0.95 -> 1 ramp
 * that was asked for. Killing the transition only while PENDING is set is not
 * enough, because PENDING is removed the instant the tween starts. ANIM covers
 * the tween itself and is dropped on completion, which hands the hover lift
 * back untouched. */
const itemSelectors = (attr) =>
  SECTIONS.map(c => `#page-home #${c.id} [${attr}] ${c.itemSel}`).join(',\n')
const PENDING_CSS = `
${itemSelectors(PENDING)} { opacity: 0; }
${itemSelectors(PENDING)},
${itemSelectors(ANIM)} { transition: none; }
`
if (typeof document !== 'undefined' && !document.getElementById('pb-scroll-reveals-css')) {
  const el = document.createElement('style')
  el.id = 'pb-scroll-reveals-css'
  el.textContent = PENDING_CSS
  document.head.appendChild(el)
}

/** Suppress the pre-existing reveal so GSAP is the only thing animating. */
function takeOver(section) {
  section.dataset.rvPlayed = '1'
  section.classList.add('in')
}

/* initIndustryReveal() in Pentabay.html is a purpose-built reveal for this one
   grid: an IntersectionObserver that toggles .is-in, with the hidden state and
   a per-card `transition-delay: calc(var(--i) * 70ms)` in CSS behind the
   [data-ind-anim="1"] gate. Measured live before touching it: indInit=1,
   data-ind-anim=1, and card 4 computing transitionDelay 0.21s (3 x 70ms).
   Both halves have to go, in the right order:
     - indInit='1'  makes the initialiser bail if it has not run yet
     - removing data-ind-anim disables the CSS if it already has
   Its own comment says "Never move the hidden state onto .ind-card directly",
   because the gate is what stops a failed script stranding cards at opacity 0.
   PENDING_CSS keeps that property - the hidden state lives behind an attribute
   this file removes at trigger time, so the failure mode is still "cards
   visible", never "cards invisible". */
function takeOverIndustryGrid(grid) {
  grid.dataset.indInit = '1'
  grid.removeAttribute('data-ind-anim')
  grid.classList.remove('is-in')
}

/* Same regex runCounters() uses, so "100+" -> ('', 100, '+'), "85%" -> ('', 85,
   '%'), "5.0" -> ('', 5.0, '') with one decimal preserved. Anything
   non-numeric ("AODA / AA") returns null and is left alone, exactly as the old
   code did. */
const NUM_RE = /^(\D*)(\d[\d,]*(?:\.\d+)?)(.*)$/

function parseStat(el) {
  const m = (el.textContent || '').trim().match(NUM_RE)
  if (!m) return null
  const raw = m[2].replace(/,/g, '')
  return {
    el,
    prefix: m[1],
    suffix: m[3],
    target: parseFloat(raw),
    decimals: (raw.split('.')[1] || '').length,
  }
}

const renderStat = (s, value) => {
  s.el.textContent = s.prefix + value.toFixed(s.decimals) + s.suffix
}

/* Both guards, deliberately. runCounters() has two passes: the first skips on
   countInit, the second iterates [data-count] and skips on countObserved.
   Setting only one leaves the other pass live depending on whether the router
   has already run, so this must be order-independent. data-count is never set
   here - without it the second pass cannot select the element at all. */
function takeOverCounters(stats) {
  for (const s of stats) {
    s.el.dataset.countInit = '1'
    s.el.dataset.countObserved = '1'
  }
}

export default function ScrollReveals() {
  const done = useRef(false)

  useGSAP(() => {
    if (done.current) return
    if (window.location.pathname !== '/') return

    let observer = null
    let timeoutId = 0
    const built = new Set()

    const build = () => {
      for (const cfg of SECTIONS) {
        if (built.has(cfg.id)) continue
        const section = document.querySelector(`#page-home #${cfg.id}`)
        if (!section) continue
        const trigger = typeof cfg.triggerSel === 'function'
          ? cfg.triggerSel(section)
          : section.querySelector(cfg.triggerSel)
        const items = trigger ? Array.from(trigger.querySelectorAll(cfg.itemSel)) : []
        if (!trigger || !items.length) continue

        built.add(cfg.id)
        takeOver(section)
        if (cfg.legacy === 'industryGrid') takeOverIndustryGrid(trigger)

        // Parsed BEFORE anything can rewrite the text. If runCounters() were
        // left live and mid-flight, textContent would be a partial value and
        // the target would be parsed wrong.
        const stats = cfg.countSel
          ? Array.from(trigger.querySelectorAll(cfg.countSel)).map(parseStat).filter(Boolean)
          : []
        if (stats.length) takeOverCounters(stats)

        const mm = gsap.matchMedia()

        mm.add('(prefers-reduced-motion: reduce)', () => {
          trigger.removeAttribute(PENDING)
          trigger.removeAttribute(ANIM)
          gsap.set(Array.from(trigger.querySelectorAll(cfg.itemSel)), { clearProps: 'all' })
          // Land on the final figure with no climb - same choice runCounters
          // makes under `reduce`.
          for (const st of stats) renderStat(st, st.target)
        })

        mm.add('(prefers-reduced-motion: no-preference)', () => {
          trigger.setAttribute(PENDING, '')

          const st = ScrollTrigger.create({
            trigger,
            start: cfg.start || DEFAULT_START,
            once: true,   // entrance, not a scrub - never replay
            onEnter() {
              // Resolved HERE, not at build time: the marquee clones exist by
              // now, so they animate with the originals instead of being left
              // behind at opacity 0. See the PENDING_CSS note above.
              const live = Array.from(trigger.querySelectorAll(cfg.itemSel))
              trigger.removeAttribute(PENDING)
              trigger.setAttribute(ANIM, '')

              /* Count-up. gsap.to() on a plain object with onUpdate, so the
                 tween owns a number and this only formats it - no DOM value is
                 read back mid-flight.
                 Each stat starts in step with ITS OWN card (index * stagger)
                 rather than all four firing at once, which is what keeps the
                 climb reading as part of the card arriving. The count is longer
                 than the fade (1.5s vs 0.7s) on purpose, so it is still moving
                 after the card has settled. */
              for (const st of stats) {
                const card = st.el.closest(cfg.itemSel)
                const i = card ? live.indexOf(card) : 0
                const counter = { v: 0 }
                renderStat(st, 0)
                gsap.to(counter, {
                  v: st.target,
                  duration: cfg.countDuration,
                  ease: cfg.countEase,
                  delay: Math.max(0, i) * cfg.stagger,
                  onUpdate: () => renderStat(st, counter.v),
                  // Float easing can land on 99.9999; pin the exact value, then
                  // fire the shared landing punch. PB_numLand is defined beside
                  // runCounters() in Pentabay.html so the vanilla counter and
                  // this one use one class and one set of numbers; guarded
                  // because that script is injected by HtmlPage and this
                  // component can outlive a route swap.
                  onComplete: () => {
                    renderStat(st, st.target)
                    if (typeof window.PB_numLand === 'function') window.PB_numLand(st.el)
                  },
                })
              }

              gsap.fromTo(live, { ...cfg.from }, {
                ...resetFor(cfg.from),
                duration: DURATION,
                ease: cfg.ease || EASE,
                stagger: cfg.stagger,
                // Hand the elements back to their own styles - .tq-card's
                // :hover lift, and any clone taken from a .cc-logo later.
                onComplete() {
                  gsap.set(Array.from(trigger.querySelectorAll(cfg.itemSel)), {
                    clearProps: 'opacity,transform,willChange',
                  })
                  trigger.removeAttribute(ANIM)
                },
              })
            },
          })
          return () => { st.kill(); trigger.removeAttribute(PENDING); trigger.removeAttribute(ANIM) }
        })
      }
      return built.size === SECTIONS.length
    }

    if (build()) { done.current = true; return }

    observer = new MutationObserver(() => {
      if (!build()) return
      done.current = true
      observer.disconnect()
      observer = null
    })
    observer.observe(document.body, { childList: true, subtree: true })

    timeoutId = window.setTimeout(() => {
      if (observer) { observer.disconnect(); observer = null }
    }, 10000)

    return () => {
      if (observer) observer.disconnect()
      window.clearTimeout(timeoutId)
    }
  }, { dependencies: [] })

  return null
}
